/* eslint-disable max-lines-per-function */
import { Text } from '@vkontakte/vkui';
import classNames from 'clsx';
import React, { memo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { overquotaAbTariffB, overquotaAbTariffC } from 'reactApp/appHelpers/featuresHelpers/features/overquotaAbTariff';
import { overquotaAfterClear } from 'reactApp/appHelpers/featuresHelpers/features/overquotaAfterClear';
import { overquotaCommunicationPayPlacement } from 'reactApp/appHelpers/featuresHelpers/features/overquotaCommunications';
import { overquotaNewCleared } from 'reactApp/appHelpers/featuresHelpers/features/overquotaNewCleared';
import { overquotaPayRestore } from 'reactApp/appHelpers/featuresHelpers/features/overquotaPayRestore';
import { subscriptionsForDeletedUsers } from 'reactApp/appHelpers/featuresHelpers/features/subscriptionsForDeletedUsers';
import { renderNewComfirmationDialog } from 'reactApp/components/BaseConfirmDialog/BaseConfirmDialog.helpers';
import { EButtonType } from 'reactApp/components/BaseConfirmDialog/BaseConformationDialog.types';
import { QUOTA_PAGE_BASE_URL } from 'reactApp/constants/magicIdentificators';
import { PaymentUTM } from 'reactApp/constants/paymentUTM';
import { useBuyClickHandlers } from 'reactApp/hooks/useBuyClickHandlers';
import { loadProductsAndOpenFastCheckout } from 'reactApp/modules/payment/payment.module';
import { requestDataRestore, setClearedUserProcessed, setProfileUblockUser } from 'reactApp/modules/profile/profile.module';
import { historyPush } from 'reactApp/modules/router/router.module';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import type { Product } from 'reactApp/types/Billing';
import { Button } from 'reactApp/ui/Button/Button';
import {
    OverquotaBannerCtrl,
    sendDwhOverquota,
    sendRecoveryFlowDwh,
    sendTechOverqRadar,
} from 'reactApp/ui/OverquotaModal/OverquotaModal.helpers';
import type { PlaceType } from 'reactApp/ui/OverquotaModal/OverquotaModal.types';
import styles from 'reactApp/ui/OverquotaPopup/newSplash/OverquotaSplash.css';
import { getModalName } from 'reactApp/ui/OverquotaPopup/newSplash/OverquotaSplash.helpers';
import type { ModeType } from 'reactApp/ui/OverquotaPopup/newSplash/OverquotaSplash.types';
import { sendXray } from 'reactApp/utils/ga';

const getButtonSuffix = () => {
    if (overquotaAbTariffB) {
        return ' за 1 ₽';
    }
    if (overquotaAbTariffC) {
        return ' со скидкой';
    }

    return '';
};

export const Footer: React.FC<{
    mode?: ModeType;
    onClose(): void;
    storage: EStorageType;
    product?: Product;
    productForQuotaId?: string;
    place?: PlaceType;
    isMobile: boolean;
}> = memo(({ mode, storage, onClose, product, productForQuotaId, place, isMobile }) => {
    const dispatch = useDispatch();
    const email = useSelector(UserSelectors.getEmail);
    const name = getModalName(mode);

    const paySourceName = mode === 'full_blocked' ? 'mnz_overquota_splash_94day' : 'mnz_overquota_splash_31day';
    const { showPaywall } = useBuyClickHandlers({ paySource: paySourceName });

    const onGoToCloudHandler = useCallback(() => {
        if (subscriptionsForDeletedUsers) {
            sendXray(['overquota', 'gotocloud', 'click', 'force']);
        }
        sendDwhOverquota({ action: 'click', storage, place, control: OverquotaBannerCtrl.go_cloud });
        dispatch(setClearedUserProcessed({ forceLA: subscriptionsForDeletedUsers }));
        sendTechOverqRadar({ state: mode, action: 'click', bttnName: 'gotocld' });
        onClose?.();
    }, [onClose, place]);

    const onUnblockHandler = useCallback(() => {
        sendDwhOverquota({ action: 'click', storage, place, control: OverquotaBannerCtrl.unblocked, name });
        dispatch(setProfileUblockUser());
        sendTechOverqRadar({ state: mode, action: 'click', bttnName: 'unblock' });
    }, [place]);

    const onWhyWhyWhy = useCallback(() => {
        sendDwhOverquota({ action: 'click', storage, place, control: OverquotaBannerCtrl.whyWhyWhy });
        sendTechOverqRadar({ state: mode, action: 'click', bttnName: 'whywhy' });
    }, [place]);

    const onWhyNew = useCallback(() => {
        onWhyWhyWhy();
        open(`https://help.mail.ru/cloud_web/faq/?${PaymentUTM.overquotaSplashNewCleared}#overquota`, '_blank');
        sendTechOverqRadar({ state: mode, action: 'click', bttnName: 'whynew' });
    }, [onWhyWhyWhy]);

    const onRestoreHandle = useCallback(() => {
        sendRecoveryFlowDwh({ action: 'click_buy', storage });

        if (overquotaPayRestore) {
            if (!product) {
                dispatch(historyPush({ id: QUOTA_PAGE_BASE_URL }));
                return;
            }

            dispatch(
                loadProductsAndOpenFastCheckout({
                    productId: product.id,
                    paySource: 'quota_splash_restore',
                    hideOtherTariffs: true,
                    onSuccess: () => {
                        dispatch(requestDataRestore());
                    },
                })
            );
        } else {
            dispatch(requestDataRestore());
        }
        sendTechOverqRadar({ state: mode, action: 'click', bttnName: 'restore' });
    }, [product]);

    const onAddSpaceHandler = useCallback(() => {
        if (mode === 'full_blocked') {
            sendDwhOverquota({
                action: 'click',
                storage,
                place: 'onboarding',
                control: OverquotaBannerCtrl.add_space,
                source: 'file',
                name,
            });
        } else {
            sendDwhOverquota({ action: 'click', storage, place, control: OverquotaBannerCtrl.add_space, name });
        }

        if (overquotaCommunicationPayPlacement === 'paywall') {
            showPaywall();
            return;
        }

        if (!productForQuotaId) {
            dispatch(historyPush({ id: QUOTA_PAGE_BASE_URL }));
            return;
        }

        const obj = {
            productId: productForQuotaId,
            title: '',
            paySource: 'quota_splash_restore',
        };

        if (overquotaAbTariffB) {
            obj.title = 'Подписка Mail Space на месяц за 1 ₽';
        } else if (overquotaAbTariffC) {
            obj.title = 'Подписка Mail Space на год со скидкой';
        }

        dispatch(
            loadProductsAndOpenFastCheckout({
                ...obj,
                hideOtherTariffs: true,
                onSuccess: () => {
                    onClose?.();
                },
            })
        );
        sendTechOverqRadar({ state: mode, action: 'click', bttnName: 'addspc' });
    }, [onClose, place, productForQuotaId, mode]);

    const onPurgeHandler = useCallback(() => {
        sendRecoveryFlowDwh({ action: 'click_clear', storage });

        renderNewComfirmationDialog({
            renderHeader: () => 'Очистить Облако?',
            renderContent: () => <Text className={styles.dialogText}>После этого восстановить файлы не получится</Text>,
            buttons: [EButtonType.action, EButtonType.cancel],
            actionButtonText: 'Очистить навсегда',
            closeOnDimmerClick: true,
            isNegative: true,
            onAction: () => {
                sendRecoveryFlowDwh({ control: 'overquota_recovery_popup', action: 'click_clear', storage });
                dispatch(setClearedUserProcessed({ forceLA: subscriptionsForDeletedUsers }));
            },
            dataQAId: 'confirm-cloud-purge',
        });
        sendTechOverqRadar({ state: mode, action: 'click', bttnName: 'purge' });
    }, []);

    const onGoToClearHandler = useCallback(() => {
        if (mode === 'full_blocked') {
            sendDwhOverquota({
                action: 'click',
                storage,
                place: 'onboarding',
                control: OverquotaBannerCtrl.clear,
                source: 'file',
                name,
            });
        }

        dispatch(historyPush({ id: QUOTA_PAGE_BASE_URL, hash: 'cleaner' }));
        sendTechOverqRadar({ state: mode, action: 'click', bttnName: 'gotoclear' });
    }, [onClose]);

    const handleChangeAccountClick = useCallback(() => {
        const path = storage === EStorageType.home ? '/home' : window.location.pathname;

        const successPage = `${window.location.origin}${path}`;

        window.__PH.authForm.show({
            formType: 'relogin',
            successPage,
            email,
        });
        sendTechOverqRadar({ state: mode, action: 'click', bttnName: 'accchn' });
    }, [email, storage]);

    if (mode === 'cleared' && overquotaNewCleared) {
        return (
            <div className={classNames(styles.buttons, styles.buttonsNewClear)}>
                <Button className={styles.button} data-name="freeze-renewal" theme="vk" onClick={onGoToCloudHandler} primary>
                    Перейти в Облако
                </Button>
                <Button className={styles.button} onClick={onWhyNew} theme="octavius" data-name="secondary">
                    Узнать -- почему?
                </Button>
                {Boolean(isMobile) && (
                    <Button
                        className={classNames(styles.button, styles.button_tetriary)}
                        onClick={handleChangeAccountClick}
                        theme="octavius"
                        data-name="secondary"
                    >
                        Войти в другой аккаунт
                    </Button>
                )}
            </div>
        );
    }

    if (mode === 'cleared') {
        return (
            <div className={classNames(styles.buttons, styles.buttons_column)}>
                <Button className={styles.button} data-name="freeze-renewal" theme="vk" onClick={onGoToCloudHandler} primary>
                    Перейти в Облако
                </Button>
                <a
                    className={styles.link}
                    href={`https://help.mail.ru/cloud_web/faq/?${PaymentUTM.overquotaSplashNewCleared}#overquota`}
                    onClick={onWhyWhyWhy}
                    target="_blank"
                    rel="noreferrer noopener"
                >
                    Почему это произошло?
                </a>
            </div>
        );
    }

    if (mode === 'after_clear' && overquotaAfterClear) {
        return (
            <div className={styles.buttons}>
                <Button className={styles.button} data-name="freeze-renewal-after-clear" theme="vk" onClick={onRestoreHandle} primary>
                    {overquotaPayRestore ? 'Вернуть доступ с подпиской' : 'Снять ограничение'}
                </Button>
                <Button className={styles.button} onClick={onPurgeHandler} theme="octavius" data-name="secondary">
                    Очистить Облако
                </Button>
            </div>
        );
    }

    if (mode === 'full_blocked') {
        return (
            <div className={styles.buttons}>
                <Button className={styles.button} data-name="freeze-renewal" theme="octavius" onClick={onAddSpaceHandler} primary>
                    Вернуть доступ {getButtonSuffix() || 'с подпиской'}
                </Button>
                <Button className={styles.button} onClick={onGoToClearHandler} theme="octavius" data-name="secondary">
                    Удалить лишнее
                </Button>
                {Boolean(isMobile) && (
                    <Button
                        className={classNames(styles.button, styles.button_tetriary)}
                        onClick={handleChangeAccountClick}
                        theme="octavius"
                        data-name="secondary"
                    >
                        Войти в другой аккаунт
                    </Button>
                )}
            </div>
        );
    }

    return (
        <div className={styles.buttons}>
            <Button className={styles.button} data-name="freeze-renewal" theme="octavius" onClick={onAddSpaceHandler} primary>
                Увеличить место {getButtonSuffix()}
            </Button>
            <Button className={styles.button} onClick={onUnblockHandler} theme="octavius" data-name="secondary">
                Снять ограничение временно
            </Button>
            {Boolean(isMobile) && (
                <Button
                    className={classNames(styles.button, styles.button_tetriary)}
                    onClick={handleChangeAccountClick}
                    theme="octavius"
                    data-name="secondary"
                >
                    Войти в другой аккаунт
                </Button>
            )}
        </div>
    );
});

Footer.displayName = 'Footer';
